/**
 * Auto-aprendizaje de endpoints `/api/*` que la app consume. Cada
 * respuesta GET 2xx que pasa por axios se registra en localStorage;
 * el próximo warmup la incluye automáticamente.
 *
 * Por qué esto en vez de una lista hardcoded:
 *   - El proyecto añade endpoints nuevos constantemente. Mantener
 *     una lista manual queda obsoleta rápido y nadie se acuerda.
 *   - El usuario solo necesita offline para lo que USA — un endpoint
 *     que nunca visita no necesita warmup.
 *   - Cubre automáticamente futuras páginas con nuevos endpoints.
 *
 * Cómo funciona:
 *   - `recordApiCall(url)` se llama desde el response interceptor de
 *     axios cuando un GET resuelve con 2xx.
 *   - Persiste el path canónico (sin query string variable) en
 *     localStorage bajo `boosters_ai_learned_apis`. Algunos endpoints
 *     LIST se guardan con su query string mínima (page=1&limit=50)
 *     para que el warmup pida una primera página coherente.
 *   - `getLearnedApis()` devuelve la lista para el warmup hook.
 *
 * Seguridad y privacidad:
 *   - Solo guarda PATHS, no respuestas ni IDs sensibles.
 *   - El cap de 200 entries evita crecimiento ilimitado.
 *   - Logout limpia esta clave junto al resto del client state.
 */

const STORAGE_KEY = 'boosters_ai_learned_apis';
const MAX_LEARNED = 200;

/// Paths del /api/* que NUNCA queremos aprender ni warmup-ear,
/// porque son inestables o no aportan valor offline:
///   - /auth/*: tokens, no cachear (lo evita axios interceptor además).
///   - /notifications: real-time, alta rotación, PII per-user.
///   - /audit: log de auditoría, sensible.
///   - /health: healthcheck, debe golpear red real.
///   - URLs con UUID en path (detalles específicos): los warm-ups
///     deben ser listas, no items individuales (los items se cachean
///     orgánicamente cuando el user los abre).
const DISCOVERY_DENY = [
  /^\/api\/auth\b/,
  /^\/api\/notifications\b/,
  /^\/api\/audit\b/,
  /^\/api\/health\b/,
];

/// Detecta UUIDs (v4) en el path para no aprender URLs con IDs
/// específicos como warmup candidate. P. ej. `/api/users/<uuid>`
/// no entra como warmup, pero `/api/users` sí.
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;

export function recordApiCall(url: string): void {
  if (typeof window === 'undefined') return;
  if (!url) return;

  // Normaliza al formato `/api/...` (axios baseURL puede prefijar
  // `http://host/api` o solo `/api`).
  let path = url;
  try {
    if (path.startsWith('http')) {
      const u = new URL(path);
      path = u.pathname + (u.search || '');
    }
  } catch {
    // URL inválida, dejamos como está
  }
  if (!path.startsWith('/api/')) return;

  // Aplica denylist sobre el path sin query.
  const pathOnly = path.split('?')[0];
  for (const re of DISCOVERY_DENY) {
    if (re.test(pathOnly)) return;
  }
  // Excluye detalles individuales (path con UUID).
  if (UUID_RE.test(pathOnly)) return;

  // Para endpoints de listado conservamos una query mínima estable
  // (`page=1&limit=50`) — si el caller usa otra paginación, todavía
  // el path base funciona; el warmup pide la primera página.
  const canonical = canonicalize(path);
  if (!canonical) return;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const arr: string[] = raw ? JSON.parse(raw) : [];
    if (arr.includes(canonical)) return; // ya lo tenemos
    arr.push(canonical);
    // Recorta si supera el cap (descarta los más antiguos).
    const final = arr.length > MAX_LEARNED ? arr.slice(arr.length - MAX_LEARNED) : arr;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(final));
  } catch {
    // QuotaExceededError o storage deshabilitado → no aprendemos
    // esta sesión, no es crítico.
  }
}

/// Lee la lista actual de APIs aprendidas para el warmup.
export function getLearnedApis(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

/// Borra la lista. Lo llama `clearPersistedClientState` en logout
/// para evitar fugas entre usuarios del mismo browser.
export function clearLearnedApis(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/// Devuelve la versión canónica de un path /api/* para warmup:
///   - GET de un listado con `page=N&limit=M` → reduce a la
///     primera página con limit=50 para que el warmup sea
///     determinista.
///   - GET de un recurso con `?foo=bar` distintos cada vez → quita
///     query string entera y warmup pide solo el path base.
function canonicalize(path: string): string | null {
  const [base, query] = path.split('?');
  if (!base.startsWith('/api/')) return null;
  // Si el path tiene query string, conservamos solo si parece
  // paginación estándar `page=1&limit=50`. Si no, lo dejamos sin
  // query (más conservador).
  if (!query) return base;
  const params = new URLSearchParams(query);
  const hasPage = params.has('page');
  const hasLimit = params.has('limit');
  if (hasPage || hasLimit) {
    return `${base}?page=1&limit=50`;
  }
  return base;
}
